import React from "react";
import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import messages from "./messages";
import { useTheme, Box, Paper, CircularProgress, IconButton } from "@mui/material";
import { tokens } from "../../../assets/theme";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import ToolServiceStatusCell from "../../cmms/components/ToolServiceStatusCell";
import cmmsMessages from "../../cmms/cmmsMessages";

export default function ToolsDataGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS

    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

    let columns = [
      { 
        field: "name", 
        headerName: formatMessage(messages.nameColumnLabel),
        flex: 1,
      },
      {
        field: "description",
        headerName: formatMessage(messages.descriptionColumnLabel),
        flex: 4,
      },
      // CMMS: how far the tool has run against its service schedule. Only present when the
      // module is switched on — this page is OEE's, and every install without CMMS should see
      // the grid it has always seen.
      ...(props.showServiceStatus
        ? [{
            field: "serviceStatus",
            headerName: formatMessage(cmmsMessages.toolStatusColumn),
            width: 190,
            /*
             * Sorts on the number so "which of my tools are overdue" can be asked of a
             * register with a thousand rows. Without a valueGetter the column would sort on
             * row.serviceStatus, which is undefined for every row, and the question the
             * column exists to answer could not be put to it.
             *
             * -1 puts unwatched tools below a genuine 0%.
             */
            valueGetter: (params) =>
              props.serviceStatusByToolId?.get(params.row.id)?.percentConsumed ?? -1,
            renderCell: (params) => (
              <ToolServiceStatusCell
                status={props.serviceStatusByToolId?.get(params.row.id)}
                isLoading={props.serviceStatusLoading}
              />
            ),
          }]
        : []),
      // CMMS: when this tool was last serviced. Gated on the same flag as the column above and
      // rendered for every row, including tools no schedule watches — "never serviced" is an
      // answer, and gating the button on the status reading would hide the history of exactly
      // the tools that are no longer watched but were serviced in the past.
      ...(props.showServiceStatus
        ? [{
            field: "serviceHistory",
            headerName: formatMessage(cmmsMessages.toolHistoryColumn),
            width: 60,
            // Nothing to sort or filter on: the cell is an action, and the row carries no
            // history field to compare. Left sortable it would silently reorder on undefined.
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label={formatMessage(cmmsMessages.toolHistoryAction, { tool: params.row.name })}
                size="small"
                sx={{
                    "&:hover": {
                    backgroundColor: colors.primary[500],
                    },
                    backgroundColor: colors.primary[700],
                }}
                onClick={() => props.onShowServiceHistory(params.row)}
              >
                <HistoryIcon fontSize="inherit" />
              </IconButton>
            ),
          }]
        : []),
      {
        field:"edit",
        headerName:"",
        renderCell:(params) => {
            return(
                <>
                <IconButton
                    aria-label={formatMessage(messages.editToolLabel)}
                    size="small"
                    sx={{
                        "&:hover": {
                        backgroundColor: colors.primary[500],
                        },
                        backgroundColor: colors.primary[700],
                    }}
                    onClick={() => {
                        props.handleEditToolClick(params.row.id)
                    }}
                    >
                        <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                    aria-label={formatMessage(messages.deleteToolLabel)}
                    size="small"
                    sx={{
                        "&:hover": {
                        backgroundColor: colors.primary[500],
                        },
                        backgroundColor: colors.primary[700],
                        marginLeft: "10px"
                    }}
                    onClick={() => {
                      props.handleDeleteToolClick(params.row.id)
                    }}
                    >
                        <DeleteIcon fontSize="inherit" />
                </IconButton>
                </>
            )
        },
        width: 90,
      },
    ];
  
    const columnOrderLocalStorageName = 'tools-columnOrder';
    const columnWidthsLocalStorageName = 'tools-columnWidths';
    
    // Load persisted column order, widths, and sort order on mount
    function initColumnsFromLocalStorage(columns) {
      let columnsToFix = columns;
      // Load column order
      const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
      if (savedColumnOrder) {
        const orderArray = JSON.parse(savedColumnOrder);
        columnsToFix = reorderColumns(orderArray, columnsToFix);
      }

      // Load column widths
      const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
      if (savedWidths) {
          const widthMap = JSON.parse(savedWidths);
          columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
      return columnsToFix;
    };
  
  
    // Reorder columns based on saved order
    //
    // filter(Boolean) is load-bearing: a saved order can name a column that is not in the
    // current set, and `find` then yields undefined, which DataGridPro crashes on when it
    // reads .field off it. That became reachable the moment the service status column was
    // made conditional — save an order with CMMS on, reload with it off. The CMMS grids next
    // door (WorkOrdersDataGrid, RecurringSchedulesDataGrid) already guard it the same way.
    const reorderColumns = (orderArray, columnsToFix) => {
      const reorderedColumns = orderArray
          .map((field) => columnsToFix.find((col) => col.field === field))
          .filter(Boolean);
      reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
      return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
      const updatedColumns = columnsToFix.map((col) => ({
        ...col,
        width: widthMap[col.field] || col.width, 
        flex: widthMap[col.field] !== undefined ? undefined : col.flex, 
      }));
      return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
      if (!apiRef.current) {
        return;
      }
      const orderedColumns = apiRef.current.getVisibleColumns();
      const newColumnOrder = orderedColumns.map((col) => col.field);
      localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
      const { colDef, width } = params;
      const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
      newWidths[colDef.field] = width;
      localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };

    if(props.tools.isLoading) {
        return <Box padding={2}>
          <Paper
            sx={{
              position: "relative",
              borderRadius: "4px",
            }}
          >
            <CircularProgress color="secondary" />
          </Paper>
        </Box>
     }

     columns = initColumnsFromLocalStorage(columns);

     return (
      <Box padding={2}>
        <Paper
          sx={{
            position: "relative",
            borderRadius: "4px",
          }}
        >
          <Box 
            m="0px 0 0 0"
            height="76vh"
            sx={{
                "& .MuiDataGrid-root": {
                    backgroundColor: colors.primary[400],
                    borderColor: `${colors.primary[700]} !important`,
                    borderRadius: "0px",
                    fontSize: "1.0rem",
                    border: "0 !important",
                },
                "& .MuiDataGrid-main": {
                    borderRadius: "0px",
                    border: "0 !important",
                    borderColor: `${colors.primary[700]} !important`,
                },
                "& .MuiDataGrid-cell": {
                    borderColor: `${colors.primary[700]} !important`,
                    borderWidth: "2px !important",
                },
                "& .name.column--cell": {
                    color: colors.greenAccent[300],
                },
                "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.primary[400],
                    fontSize: '1.0rem',
                    borderColor: `${colors.primary[700]} !important`,
                    borderWidth: "2px"
                },
                "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: colors.primary[400],
                },
                "& .MuiDataGrid-footerContainer": {
                    borderTop: "none",
                    backgroundColor: colors.primary[600],
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                    color: `${colors.gray[100]} !important`,
                },
                "& .super-app-theme--11": {
                    bgcolor: `${colors.redAccent[500]} !important`,
                },
                "& .super-app-theme--undefined-stop": {
                    bgcolor: `${colors.redAccent[500]} !important`,
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                    opacity: 0,
                    transition: "opacity 0.2s"
                  },
                  
                "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                    opacity: 1
                },
              }}
          >
            { (!props.tools.isLoading) && <DataGridPro
              apiRef={apiRef}
              localeText={translations.components.MuiDataGrid.defaultProps.localeText}
              onColumnResize={handleColumnResize}
              onColumnOrderChange={handleColumnOrderChange}
              rows={props.tools.data}
              getRowId={ (row) => row.id }
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              slotProps={{
                  panel: {
                  sx: {
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#799c4a",
                          opacity: 0.3,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                          backgroundColor: "#799c4a",
                      },
                      "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                          color: colors.primary[200],
                          fontSize: "0.75rem",
                          "&:hover": {
                              color: colors.primary[300],
                          },
                      },
                      "& .MuiFormLabel-root.Mui-focused": {
                          color: colors.primary[200],
                      },
                  },
                  }
              }}
            /> }
            { (props.tools.isLoading) && <CircularProgress color="secondary" />}
          </Box>
        </Paper>
      </Box>
    );
}